import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bot, ArrowLeft, Send, User, Sparkles, Scale, Ruler, Calendar,
  Activity, Target, AlertTriangle, DollarSign, Utensils, Zap,
  Save, ToggleLeft, ToggleRight, MessageSquare, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentario', desc: 'Poco o nada de ejercicio' },
  { value: 'light', label: 'Ligero', desc: '1-3 días/semana' },
  { value: 'moderate', label: 'Moderado', desc: '3-5 días/semana' },
  { value: 'active', label: 'Activo', desc: '6-7 días/semana' },
  { value: 'very_active', label: 'Muy activo', desc: 'Atleta, 2x al día' },
];

const GOALS = [
  { value: 'lose_weight', label: 'Perder peso', icon: '🏃' },
  { value: 'maintain', label: 'Mantener', icon: '⚖️' },
  { value: 'gain_muscle', label: 'Ganar músculo', icon: '💪' },
];

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Desayuno' },
  { value: 'snack', label: 'Snack' },
  { value: 'lunch', label: 'Comida' },
];

const DAYS = [
  { value: 'monday', label: 'Lun' },
  { value: 'tuesday', label: 'Mar' },
  { value: 'wednesday', label: 'Mié' },
  { value: 'thursday', label: 'Jue' },
  { value: 'friday', label: 'Vie' },
];

const DIETARY_RESTRICTIONS = [
  'Vegetariano', 'Vegano', 'Sin gluten', 'Sin lactosa', 'Keto', 'Bajo en sodio'
];

const COMMON_ALLERGIES = [
  'Nueces', 'Maní', 'Mariscos', 'Huevo', 'Soya', 'Trigo'
];

export default function NutritionSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    weight_kg: '',
    height_cm: '',
    age: '',
    gender: 'male',
    activity_level: 'moderate',
    goal: 'maintain',
    allergies: [],
    dietary_restrictions: [],
    monthly_budget: '',
    service_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    meal_types: ['lunch']
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }

    fetchProfile();
    fetchChatHistory();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/nutrition/profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setProfile(data);
          setForm({
            weight_kg: data.weight_kg?.toString() || '',
            height_cm: data.height_cm?.toString() || '',
            age: data.age?.toString() || '',
            gender: data.gender || 'male',
            activity_level: data.activity_level || 'moderate',
            goal: data.goal || 'maintain',
            allergies: data.allergies || [],
            dietary_restrictions: data.dietary_restrictions || [],
            monthly_budget: data.monthly_budget?.toString() || '',
            service_days: data.service_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            meal_types: data.meal_types || ['lunch']
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await fetch(`${API}/nutrition/chat/history`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const formattedMessages = data.reverse().flatMap(item => [
          { role: 'user', content: item.user_message },
          { role: 'assistant', content: item.assistant_response }
        ]);
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!form.weight_kg || !form.height_cm || !form.age || !form.monthly_budget) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/nutrition/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          weight_kg: parseFloat(form.weight_kg),
          height_cm: parseFloat(form.height_cm),
          age: parseInt(form.age),
          monthly_budget: parseFloat(form.monthly_budget)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        toast.success('Perfil nutricional guardado');
      } else {
        throw new Error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAgent = async () => {
    try {
      const response = await fetch(`${API}/nutrition/agent/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !profile?.agent_enabled })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, agent_enabled: data.agent_enabled });
        toast.success(data.agent_enabled ? 'Agente activado' : 'Agente desactivado');
      }
    } catch (error) {
      toast.error('Error al cambiar estado del agente');
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await fetch(`${API}/nutrition/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error en el chat');
      }
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setChatLoading(false);
    }
  };

  const handleGetMealPlan = async () => {
    setChatLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '¿Puedes crear un plan de comidas semanal para mí?' }]);

    try {
      const response = await fetch(`${API}/nutrition/suggest-meals`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        let responseText = '';
        
        if (data.weekly_plan) {
          responseText = `🗓️ **Plan Semanal Personalizado**\n\n`;
          data.weekly_plan.forEach(day => {
            responseText += `**${day.day}** (${day.daily_calories} kcal - $${day.daily_total?.toFixed(2)})\n`;
            day.meals.forEach(meal => {
              responseText += `• ${meal.product} - $${meal.price?.toFixed(2)} (~${meal.calories_estimate} kcal)\n`;
            });
            responseText += '\n';
          });
          responseText += `\n💰 **Total semanal:** $${data.weekly_total?.toFixed(2)} MXN\n`;
          responseText += `🔥 **Promedio diario:** ${data.weekly_calories_avg} kcal\n\n`;
          if (data.tips?.length) {
            responseText += `💡 **Tips:**\n${data.tips.map(t => `• ${t}`).join('\n')}`;
          }
        } else if (data.response) {
          responseText = data.response;
        }

        setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al generar plan');
      }
    } catch (error) {
      toast.error('Error al generar plan de comidas');
    } finally {
      setChatLoading(false);
    }
  };

  const toggleArrayItem = (array, item, setter) => {
    if (array.includes(item)) {
      setter(array.filter(i => i !== item));
    } else {
      setter([...array, item]);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="nutrition-settings">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/settings')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Agente de Nutrición</h1>
                <p className="text-stone-500">Tu asistente personal de alimentación con IA</p>
              </div>
            </div>
            {profile && (
              <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-full border border-stone-200">
                <span className="text-sm text-stone-600">Agente</span>
                <Switch
                  checked={profile.agent_enabled}
                  onCheckedChange={handleToggleAgent}
                  data-testid="agent-toggle"
                />
                <span className={`text-sm font-medium ${profile.agent_enabled ? 'text-emerald-700' : 'text-stone-400'}`}>
                  {profile.agent_enabled ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'profile' ? 'default' : 'outline'}
              className={`rounded-full ${activeTab === 'profile' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
              onClick={() => setActiveTab('profile')}
              data-testid="tab-profile"
            >
              <User className="w-4 h-4 mr-2" />
              Mi Perfil
            </Button>
            <Button
              variant={activeTab === 'chat' ? 'default' : 'outline'}
              className={`rounded-full ${activeTab === 'chat' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
              onClick={() => setActiveTab('chat')}
              disabled={!profile}
              data-testid="tab-chat"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat con Nutricionista
            </Button>
          </div>

          {activeTab === 'profile' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Biometric Data */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <Scale className="w-5 h-5 text-emerald-700" />
                  Datos Biométricos
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-stone-600 mb-1 block">Peso (kg)</label>
                      <Input
                        type="number"
                        value={form.weight_kg}
                        onChange={(e) => setForm({...form, weight_kg: e.target.value})}
                        placeholder="70"
                        className="rounded-xl"
                        data-testid="nutrition-weight"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-stone-600 mb-1 block">Altura (cm)</label>
                      <Input
                        type="number"
                        value={form.height_cm}
                        onChange={(e) => setForm({...form, height_cm: e.target.value})}
                        placeholder="175"
                        className="rounded-xl"
                        data-testid="nutrition-height"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-stone-600 mb-1 block">Edad</label>
                      <Input
                        type="number"
                        value={form.age}
                        onChange={(e) => setForm({...form, age: e.target.value})}
                        placeholder="30"
                        className="rounded-xl"
                        data-testid="nutrition-age"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-stone-600 mb-1 block">Género</label>
                      <Select
                        value={form.gender}
                        onValueChange={(v) => setForm({...form, gender: v})}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="nutrition-gender">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Masculino</SelectItem>
                          <SelectItem value="female">Femenino</SelectItem>
                          <SelectItem value="other">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-stone-600 mb-2 block">Nivel de Actividad</label>
                    <Select
                      value={form.activity_level}
                      onValueChange={(v) => setForm({...form, activity_level: v})}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="nutrition-activity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTIVITY_LEVELS.map(level => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label} - {level.desc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Goals */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-700" />
                  Objetivo
                </h3>
                
                <div className="grid grid-cols-3 gap-3">
                  {GOALS.map(goal => (
                    <button
                      key={goal.value}
                      onClick={() => setForm({...form, goal: goal.value})}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        form.goal === goal.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      data-testid={`goal-${goal.value}`}
                    >
                      <span className="text-2xl mb-2 block">{goal.icon}</span>
                      <span className="text-sm font-medium text-stone-900">{goal.label}</span>
                    </button>
                  ))}
                </div>

                {profile && (
                  <div className="mt-6 p-4 bg-emerald-50 rounded-xl">
                    <p className="text-sm text-emerald-800">
                      <Zap className="w-4 h-4 inline mr-1" />
                      Calorías diarias recomendadas: <strong>{profile.daily_calories} kcal</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Restrictions */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Restricciones y Alergias
                </h3>
                
                <div className="mb-4">
                  <label className="text-sm text-stone-600 mb-2 block">Restricciones Dietéticas</label>
                  <div className="flex flex-wrap gap-2">
                    {DIETARY_RESTRICTIONS.map(item => (
                      <button
                        key={item}
                        onClick={() => toggleArrayItem(form.dietary_restrictions, item, (arr) => setForm({...form, dietary_restrictions: arr}))}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          form.dietary_restrictions.includes(item)
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'bg-stone-100 text-stone-600 border border-stone-200 hover:bg-stone-200'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-stone-600 mb-2 block">Alergias</label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_ALLERGIES.map(item => (
                      <button
                        key={item}
                        onClick={() => toggleArrayItem(form.allergies, item, (arr) => setForm({...form, allergies: arr}))}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          form.allergies.includes(item)
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : 'bg-stone-100 text-stone-600 border border-stone-200 hover:bg-stone-200'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Budget & Schedule */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-700" />
                  Presupuesto y Horario
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-stone-600 mb-1 block">Presupuesto Mensual (MXN)</label>
                    <Input
                      type="number"
                      value={form.monthly_budget}
                      onChange={(e) => setForm({...form, monthly_budget: e.target.value})}
                      placeholder="2000"
                      className="rounded-xl"
                      data-testid="nutrition-budget"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-stone-600 mb-2 block">Días de Servicio</label>
                    <div className="flex gap-2">
                      {DAYS.map(day => (
                        <button
                          key={day.value}
                          onClick={() => toggleArrayItem(form.service_days, day.value, (arr) => setForm({...form, service_days: arr}))}
                          className={`w-12 h-12 rounded-xl font-medium transition-colors ${
                            form.service_days.includes(day.value)
                              ? 'bg-emerald-700 text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-stone-600 mb-2 block">Tipos de Comida</label>
                    <div className="flex gap-2">
                      {MEAL_TYPES.map(meal => (
                        <button
                          key={meal.value}
                          onClick={() => toggleArrayItem(form.meal_types, meal.value, (arr) => setForm({...form, meal_types: arr}))}
                          className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                            form.meal_types.includes(meal.value)
                              ? 'bg-emerald-700 text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          {meal.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="md:col-span-2">
                <Button
                  className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6"
                  onClick={handleSaveProfile}
                  disabled={saving}
                  data-testid="save-nutrition-profile"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Guardando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="w-5 h-5" />
                      Guardar Perfil Nutricional
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'chat' && profile && (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden h-[600px] flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-stone-100 bg-gradient-to-r from-emerald-50 to-stone-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-700 rounded-full flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">Nutricionista IA</p>
                      <p className="text-xs text-stone-500">Asistente de nutrición inteligente</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={handleGetMealPlan}
                    disabled={chatLoading}
                    data-testid="get-meal-plan-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generar Plan Semanal
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-emerald-700" />
                    </div>
                    <h3 className="font-semibold text-stone-900 mb-2">¡Hola! Soy tu nutricionista virtual</h3>
                    <p className="text-stone-500 mb-4">Pregúntame sobre nutrición, calorías o pídeme un plan de comidas</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {['¿Cuántas calorías necesito?', '¿Qué desayuno me recomiendas?', 'Opciones bajas en calorías'].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setChatInput(q); }}
                          className="px-3 py-1.5 bg-stone-100 rounded-full text-sm text-stone-600 hover:bg-stone-200 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-emerald-700 text-white rounded-br-md'
                          : 'bg-stone-100 text-stone-900 rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}

                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-stone-100 p-4 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-stone-100">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Escribe tu pregunta..."
                    className="rounded-full"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    disabled={chatLoading}
                    data-testid="chat-input"
                  />
                  <Button
                    className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-6"
                    onClick={handleSendMessage}
                    disabled={chatLoading || !chatInput.trim()}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
